/**
 * Abbonamenti — "Fuori Stripe".
 *
 * Il pezzo che rende la sezione onesta. Stripe incassa circa 2.175 euro al mese,
 * ma i canoni piu' grossi (Villa Fago, New Omnia, Emmequadro, Graci) non passano
 * da li': si fatturano a mano e vivono in Amministrazione come ricavi ricorrenti.
 * Mostrare solo Stripe direbbe che l'agenzia incassa un quarto di quello che
 * incassa davvero.
 *
 * COSA FA QUESTA SCHEDA E COSA NON FA PIU'.
 * Questi canoni non sono abbonamenti: un canone mensile per un lavoro
 * continuativo e' una COMMESSA pagata a rate, e le commesse si raccontano in
 * Progetti, dove c'e' l'oggetto del lavoro, il valore e la scadenza vera presi
 * dal preventivo. Tenere qui anche quel racconto voleva dire avere due pagine
 * che dicono la stessa cosa in due modi, e prima o poi due verita' diverse.
 *
 * Quindi la scheda resta, ma per la sola domanda a cui Progetti non risponde:
 * quanto entra ogni mese a cadenza fissa, Stripe e non-Stripe messi insieme.
 * Il "che cos'e'" adesso e' un rimando al progetto, non una descrizione.
 *
 * In fondo c'e' la riconciliazione, che e' una MISURA e basta. Non spegne
 * niente: le previsioni di Amministrazione si usano ogni giorno e non si toccano
 * senza avere in mano il numero prima-dopo. Questo blocco quel numero lo scrive.
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '../../components/ui/Icon';
import {
  OrdinaFiltra, perData, perNumero, perTesto, useFiltri, useOrdina, type FiltroDef, type OpzioneOrdina,
} from '../../components/ui/OrdinaFiltra';
import { fmtEur } from '../../lib/utils';
import type { Overview, OffStripeRow } from './subscriptions.types';
import './subscriptions.css';

/**
 * Ordina e filtra (24/9): la lista arriva intera con la panoramica, quindi si
 * ordina qui. Il server la manda gia' per prossima fattura: e' il predefinito.
 * Le chiavi di Filtra per (`fase`, `progetto`) sono diverse da quelle
 * dell'Elenco, che vive nello stesso indirizzo.
 */
const ORDINI: OpzioneOrdina<OffStripeRow>[] = [
  { key: 'prossima', label: 'Prossima fattura prima', criteri: [perData((r) => r.nextDueDate)] },
  { key: 'importo', label: 'Prima i canoni più alti al mese', criteri: [perNumero((r) => r.monthlyNet, 'decrescente')] },
  { key: 'cliente', label: 'Cliente dalla A alla Z', criteri: [perTesto((r) => r.clientName), perData((r) => r.nextDueDate)] },
];

const FILTRI: FiltroDef<OffStripeRow>[] = [
  {
    param: 'fase', label: 'Quando si incassa',
    opzioni: [
      { value: 'in_corso', label: 'Solo quelli in corso oggi' },
      { value: 'futuro', label: 'Solo quelli che devono ancora cominciare' },
      { value: 'concluso', label: 'Solo quelli già finiti' },
    ],
    test: (r, v) => r.phase === v,
  },
  {
    param: 'progetto', label: 'Progetto collegato',
    opzioni: [
      { value: 'con', label: 'Solo quelli legati a un progetto' },
      { value: 'senza', label: 'Solo quelli senza progetto' },
    ],
    test: (r, v) => (v === 'con' ? !!r.project : !r.project),
  },
];

const CICLO: Record<string, string> = {
  MONTHLY: 'al mese', QUARTERLY: 'a trimestre', YEARLY: "all'anno",
  WEEKLY: 'a settimana', BIMONTHLY: 'ogni 2 mesi', SEMIANNUAL: 'a semestre',
};

function mese(iso: string): string {
  const [y, m] = iso.split('-');
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
}

const data_ = (iso: string) =>
  new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' });

/**
 * La riga da sola non basta a capire se quel canone si incassa adesso.
 * Villa Fago ne ha due, il pieno fino al 10/10 e quello ridotto dal 10/11:
 * senza questa nota sembrano due canoni sommabili, e sono lo stesso contratto
 * in due momenti diversi.
 */
function nota(r: OffStripeRow): string | null {
  if (r.phase === 'futuro') return `comincia il ${data_(r.startDate)}, non è ancora nel totale`;
  if (r.phase === 'concluso') return `finito il ${data_(r.endDate!)}`;
  if (r.endDate) return `ultimo addebito il ${data_(r.endDate)}`;
  return null;
}

/**
 * Perché questo canone NON ha un progetto. Un trattino direbbe solo "manca", e
 * chi legge andrebbe a cercare un progetto che non deve esistere: qui non c'è
 * un buco da riempire, c'è un incasso che una commessa non ce l'ha.
 *
 * Due casi veri, e sono diversi fra loro:
 *  - l'affitto: subaffittiamo una stanza dell'ufficio. E' un ricavo, non un
 *    lavoro che qualcuno ci ha commissionato;
 *  - le voci "(aggregato)": una riga sola per clienti diversi, quindi non c'è
 *    un solo lavoro a cui appartenere.
 */
function senzaProgetto(r: OffStripeRow): string {
  if (r.aggregate) return 'voce cumulativa, più clienti in una riga';
  if (/affitto|sublocazion|co-?working/i.test(r.name)) return 'sublocazione dell’ufficio, non una commessa';
  return 'nessun progetto collegato';
}

export function OffStripeTab({ data }: { data: Overview }) {
  const tutte = data.offStripe?.items;
  const ordina = useOrdina(ORDINI);
  const filtri = useFiltri(FILTRI, tutte);
  const righe = useMemo(() => ordina.applica(filtri.applica(tutte ?? [])), [tutte, ordina, filtri]);

  if (!data.offStripe) {
    return (
      <div className="ed-section">
        <div className="mute text-[13px] py-6">
          I canoni fuori Stripe stanno in Amministrazione, e per vederli serve il permesso su quella sezione.
        </div>
      </div>
    );
  }

  const off = data.offStripe;
  const rec = data.reconciliation;
  const stripeMensile = data.money.mrr;
  // Il confronto si fa sul LORDO: il canone Stripe e' un addebito ivato, il
  // ricavo ricorrente e' registrato al netto. Sommare i due cosi' com'erano
  // significherebbe confrontare due cose diverse.
  // Il totale arriva dal server, che conta solo i canoni in corso oggi: rifare
  // qui la divisione per ciclo voleva dire tenere in piedi due formule uguali
  // in due posti, e contare anche i contratti che devono ancora cominciare.
  const fuoriLordo = off.monthlyGrossNominal;

  return (
    <div className="ed-section">
      <div className="adm-note">
        <Icon name="help" size={12} className="mute" />
        <span>
          Qui c'è <strong>tutto</strong> quello che si incassa a cadenza fissa, non solo Stripe. I canoni di
          questa pagina si fatturano <strong>a mano</strong>: non hanno una subscription che li addebita da sola.
          Quasi tutti pagano un <strong>progetto</strong>: <em>cosa</em> comprende il canone, quanto vale e quando
          finisce si legge lì, questa pagina dice solo quanto entra e quando.
        </span>
      </div>

      <div className="sb-vs">
        <div>
          <div className="k">Da Stripe, al mese</div>
          <div className="v">€ {fmtEur(stripeMensile)}</div>
        </div>
        <div>
          <div className="k">Fatturato a mano, al mese</div>
          <div className="v">€ {fmtEur(fuoriLordo)}</div>
        </div>
        <div>
          <div className="k">Totale ricorrente</div>
          <div className="v">€ {fmtEur(stripeMensile + fuoriLordo)}</div>
        </div>
      </div>
      <div className="mute text-[12px]" style={{ marginBottom: 20 }}>
        Cifre IVA inclusa, per poterle confrontare con l'addebito Stripe. Imponibile fuori Stripe:
        € {fmtEur(off.monthlyNetNominal)} al mese.
        {/* I contratti che devono ancora partire non entrano nel totale del mese,
            ma vanno detti: altrimenti sembra che manchino dai conti. */}
        {off.futureCount > 0 && (
          <> Nel totale non c'è {off.futureCount === 1 ? 'un canone che comincia' : `${off.futureCount} canoni che cominciano`}{' '}
          più avanti, per € {fmtEur(off.monthlyGrossFuture)} al mese.</>
        )}
      </div>

      {/* t-micro come le altre 88 intestazioni di sezione-dato del gestionale. */}
      <div className="data-hd">
        <span className="t-micro">Canoni che non passano da Stripe</span>
        {/* Il numero dice quanti se ne vedono: con un filtro acceso anche su quanti */}
        <span className="flex items-center gap-3">
          <span className="mute text-[12px]">
            {righe.length === off.items.length ? off.items.length : `${righe.length} di ${off.items.length}`}
          </span>
          <OrdinaFiltra ordina={ordina} filtri={filtri} />
        </span>
      </div>
      {/* Il totale della colonna "Imponibile" NON e' il valore dei progetti, ed
          e' la somma che verrebbe piu' facile fare guardando questa tabella.
          Il canone e' la rata, il progetto e' il contratto intero: sono lo
          stesso euro visto da due lati, e non si sommano mai. */}
      <div className="mute text-[12px]" style={{ margin: '2px 0 10px' }}>
        Gli importi qui sotto sono la <strong>rata</strong>, non il valore del contratto: il valore intero sta
        sul progetto e le due cifre non si sommano.
      </div>
      {/* sb-nolink: sul telefono ogni riga diventa una scheda, ma queste schede
          NON si aprono (un canone fuori Stripe non ha una pagina sua), quindi
          non devono portare il chevron che promette un tocco. */}
      <table className="tbl tbl--ed tbl--card sb-nolink">
        <thead>
          <tr>
            <th>Chi paga</th>
            <th>Cosa</th>
            <th style={{ width: 230 }}>Progetto</th>
            <th style={{ width: 120 }}>Ogni quanto</th>
            <th className="num" style={{ width: 120 }}>Imponibile</th>
            <th className="num" style={{ width: 120 }}>IVA inclusa</th>
            <th style={{ width: 120 }}>Prossima</th>
          </tr>
        </thead>
        <tbody>
          {righe.length === 0 && (
            <tr>
              <td colSpan={7} className="mute text-center" style={{ padding: '24px 0' }}>
                Nessun canone con questi filtri.{' '}
                <button type="button" className="sb-link" style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }} onClick={filtri.clear}>
                  Togli i filtri
                </button>
              </td>
            </tr>
          )}
          {righe.map((r) => {
            const n = nota(r);
            return (
            // Chi non e' in corso oggi resta in elenco ma si legge piu' tenue:
            // e' un contratto vero, solo non di questo mese.
            <tr key={r.id} className={r.phase === 'in_corso' ? undefined : 'sb-fuori-periodo'}>
              <td data-m="hd">
                {r.clientId ? (
                  <Link to={`/contatti/client/${r.clientId}`} className="sb-link" onClick={(e) => e.stopPropagation()}>
                    {r.clientName}
                  </Link>
                ) : (
                  <span>{r.clientName ?? '—'}</span>
                )}
              </td>
              {/* Niente text-[12px] sui <td>: non fa niente. `.tbl tbody td`
                  fissa il corpo a 14px con due elementi nel selettore e batte
                  l'utility, che ne ha uno solo. Il colore invece passa, perche'
                  per quello esiste `.tbl .mute`. E' la stessa trappola gia'
                  annotata in TasksPage.tsx. */}
              <td data-m="row" data-l="Cosa" className="mute">
                {r.name}
                {n && <span className="sb-nota">{n}</span>}
              </td>
              {/* Il lavoro che questo canone paga. Quando manca non si scrive
                  un trattino muto: il motivo per cui manca e' l'informazione. */}
              <td data-m="row" data-l="Progetto" className="mute">
                {r.project ? (
                  <Link to={`/projects/${r.project.id}`} className="sb-link" onClick={(e) => e.stopPropagation()}>
                    {r.project.name}
                  </Link>
                ) : (
                  <span className="sb-nota" style={{ marginLeft: 0 }}>{senzaProgetto(r)}</span>
                )}
              </td>
              <td data-m="key" data-l="Ogni quanto" className="mute">{CICLO[r.cycle] ?? r.cycle}</td>
              <td data-m="key" data-l="Imponibile" className="num mono">€ {fmtEur(r.amountNet)}</td>
              <td data-m="key" data-l="IVA inclusa" className="num mono">€ {fmtEur(r.amountGross)}</td>
              <td data-m="key" data-l="Prossima" className="mute mono">
                {new Date(r.nextDueDate).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' })}
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>

      {/* ── La riconciliazione ────────────────────────────────────────────── */}
      {rec && rec.aggregateItems.length > 0 && (
        <>
          <div className="ed-divider" />
          <div className="data-hd"><span className="t-micro">Attenzione: la stessa cassa contata due volte</span></div>

          <div className="mute text-[13px]" style={{ maxWidth: 780, lineHeight: 1.55, marginTop: 10 }}>
            In Amministrazione ci sono ancora <strong style={{ color: 'var(--text)' }}>{rec.aggregateItems.length} voci
            &ldquo;a corpo&rdquo;</strong> che stimavano il giro Stripe quando Stripe non era dentro Masamune OS.
            Adesso gli stessi incassi li conosciamo riga per riga, qui sopra. Finché restano accese, il forecast
            conta due volte gli stessi soldi &mdash; e li conta anche <em>in eccesso</em>, perché la stima era
            più alta dell'incassato reale.
          </div>

          <div className="sb-vs">
            <div>
              <div className="k">Le voci stimate promettono</div>
              <div className="v">€ {fmtEur(rec.aggregateMonthlyNet)}</div>
            </div>
            <div>
              <div className="k">Stripe incassa davvero</div>
              <div className="v">€ {fmtEur(rec.stripeMonthlyGross)}</div>
            </div>
            <div>
              <div className="k">Differenza, al mese</div>
              <div className="v" style={{ color: 'var(--alert)' }}>
                € {fmtEur(rec.aggregateMonthlyNet - rec.stripeMonthlyGross)}
              </div>
            </div>
          </div>

          {/* Perche' queste righe non diventano progetti come le altre: non e'
              una dimenticanza, e' che non hanno un lavoro solo a cui appartenere. */}
          <div className="mute text-[13px]" style={{ maxWidth: 780, lineHeight: 1.55, marginTop: 14 }}>
            Nessuna di queste voci ha un progetto, e non deve averlo: mettono insieme clienti diversi in una
            riga sola. <strong style={{ color: 'var(--text)' }}>Hosting clienti</strong> in particolare somma i
            rinnovi annuali di una quindicina di clienti, con scadenze sparse nell'anno e diversi già migrati
            su Stripe: resta aggregata finché non si decide di spacchettarla cliente per cliente.
          </div>

          <div className="sb-flags" style={{ marginTop: 14 }}>
            {rec.aggregateItems.map((v) => (
              <div key={v.id} className="sb-flag">
                <span className="ic"><Icon name="alert" size={12} /></span>
                <span className="tx">
                  <strong>{v.name}</strong>
                  <span className="why" style={{ display: 'block' }}>
                    € {fmtEur(v.monthlyNet)} al mese di stima, in Amministrazione &rarr; Ricavi ricorrenti.
                  </span>
                </span>
              </div>
            ))}
          </div>

          <div className="mute text-[13px]" style={{ maxWidth: 780, lineHeight: 1.55, marginTop: 18 }}>
            <strong style={{ color: 'var(--text)' }}>Il secondo doppione.</strong> Da{' '}
            {new Date(rec.since).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })} un'automazione
            emette da sola le fatture degli abbonamenti Stripe, e quelle fatture sono già dentro Masamune OS:{' '}
            <strong style={{ color: 'var(--text)' }}>{rec.invoicedCount} fatture per € {fmtEur(rec.invoicedGross)}</strong>.
            Chi somma il canone <em>e</em> la fattura conta lo stesso incasso due volte.
          </div>

          <div style={{ marginTop: 10, maxWidth: 420 }}>
            {rec.invoicedMonths.map((m) => (
              <div key={m.month} className="sb-row">
                <span className="d">{mese(m.month).slice(0, 3)}</span>
                <span className="n"><span className="t">{m.count} fatture</span></span>
                <span className="a mono">€ {fmtEur(m.gross)}</span>
              </div>
            ))}
          </div>

          <div className="adm-note" style={{ marginTop: 18 }}>
            <Icon name="lock" size={12} className="mute" />
            <span>
              Qui non è stato spento niente. Le previsioni di Amministrazione si usano ogni giorno: la scelta di
              disattivare le voci stimate la prende Lorenzo, e questi sono i numeri per prenderla.
            </span>
          </div>
        </>
      )}
    </div>
  );
}
